// Advertising platform data (7.6F.2). Search is server-side, bounded and
// debounced by the caller: the catalog is never loaded wholesale into a page.
// Compatibility always comes from the server's assessment — the browser
// never decides whether a platform/product/format is compatible.
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { api, call } from "@/api/client";
import type { CreativeProduct } from "@/api/types";

export type PlatformChoice = { readonly kind: "catalog" | "custom"; readonly id: string };

export function usePlatformSearch(orgId: string, q: string, product: CreativeProduct, enabled = true) {
  return useQuery({
    queryKey: ["org", orgId, "platform-search", product, q],
    queryFn: () =>
      call(() =>
        api.GET("/orgs/{orgId}/advertising-platforms", { params: { path: { orgId }, query: { q, product } } })
      ),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    enabled,
  });
}

export function useCompatibility(
  orgId: string,
  choice: PlatformChoice | null,
  product: CreativeProduct,
  format: string | null
) {
  return useQuery({
    queryKey: ["org", orgId, "platform-compatibility", choice?.kind, choice?.id, product, format],
    queryFn: () =>
      call(() =>
        api.GET("/orgs/{orgId}/platform-compatibility", {
          params: {
            path: { orgId },
            query: {
              ...(choice?.kind === "custom" ? { customPlatformId: choice.id } : { platformId: choice?.id ?? "" }),
              product,
              ...(format ? { format } : {}),
            },
          },
        })
      ),
    enabled: choice !== null,
    placeholderData: keepPreviousData,
  });
}

export function useDistributionCompatibility(orgId: string, distributionId: string, enabled = true) {
  return useQuery({
    queryKey: ["org", orgId, "distribution", distributionId, "compatibility"],
    queryFn: () =>
      call(() =>
        api.GET("/orgs/{orgId}/distributions/{distributionId}/compatibility", {
          params: { path: { orgId, distributionId } },
        })
      ),
    enabled,
  });
}

export function requestReview(orgId: string, choice: PlatformChoice, product: CreativeProduct) {
  return call(() =>
    api.POST("/orgs/{orgId}/platform-review-requests", {
      params: { path: { orgId } },
      body: { ...(choice.kind === "custom" ? { customPlatformId: choice.id } : { platformId: choice.id }), product },
    })
  );
}
